import { Suspense } from "react";
import { List, ListItem, ListItemText, Skeleton, styled } from "@mui/material";
import { ErrorBoundary } from "react-error-boundary";
import { DragDropContext, DropResult, Droppable } from "react-beautiful-dnd";

import { useTranslation } from "react-i18next";

import {
  useSelection,
  useUpdateSelectionMutation,
  useSelectionHasLayers,
} from "hooks/selectionHooks";

import { LayerProperties, IndicatorSelection, Selection } from "types";

import { InvisibleSelectionPopup } from "components/layers/InvisibleSelectionPopup";
import { LayerItem } from "components/layers/LayerItem";

interface Props {
  config: string;
  layerProperties: LayerProperties[];
  className: string;
}

export function SelectedLayers(props: Props) {
  const { config, layerProperties, className } = props;
  const { t } = useTranslation();
  const { data: selection } = useSelection(config, layerProperties);
  const updateSelectionMutation = useUpdateSelectionMutation(
    config,
    layerProperties
  );
  const selectionHasLayers = useSelectionHasLayers(
    config,
    selection?.indicators || [],
    layerProperties
  );

  function handleDragEnd(selection: Selection, result: DropResult) {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    function reorder(
      list: IndicatorSelection[],
      startIndex: number,
      endIndex: number
    ) {
      const result = Array.from(list);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);

      return result.map((item, i) => ({ ...item, order: i }));
    }

    const updatedIndicators = reorder(
      selection.indicators, //.sort((a, b) => (a.order > b.order ? 1 : -1)),
      result.source.index,
      result.destination.index
    );

    updateSelectionMutation.mutateAsync({
      indicators: updatedIndicators,
      properties: selection.properties,
    });
  }

  return render();

  function render() {
    if (!selection) return null;

    return (
      <Styles className={className}>
        <DragDropContext
          onDragEnd={(result: DropResult) => handleDragEnd(selection, result)}
        >
          <Droppable droppableId="droppable">
            {(provided) => (
              <List {...provided.droppableProps} ref={provided.innerRef}>
                {selection.indicators.map((selected, i) => (
                  <ErrorBoundary
                    key={[selected.indicatorId, i].join("-")}
                    fallbackRender={() => null}
                  >
                    <Suspense fallback={<Skeleton height={35} />}>
                      <LayerItem
                        config={config}
                        selection={selected}
                        layerProperties={layerProperties}
                        order={i}
                      />
                    </Suspense>
                  </ErrorBoundary>
                ))}

                {selection.indicators.length === 0 && (
                  <ListItem>
                    <ListItemText>
                      {t("message.noActiveLayersSelected")}
                    </ListItemText>
                  </ListItem>
                )}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
          {selectionHasLayers.includes(false) && (
            <InvisibleSelectionPopup config={config} />
          )}
        </DragDropContext>
      </Styles>
    );
  }
}

const Styles = styled("div")`
  display: flex;
  flex-direction: column;

  .align-bottom {
    margin-top: auto;
  }
`;
